import { memo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextTitle1, TextHeadline, TextLabel1 } from '@cbhq/cds-web/typography';
import { Icon } from '@cbhq/cds-web/icons';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { ButtonGroup } from '@cbhq/cds-web/buttons/ButtonGroup';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { ExploreListTokensMedia } from '@/components/Explore/ExploreListTokensMedia';
import { DisplayType, format } from '@/utils/numberFormatter';
import { PoolDetailsData } from './hooks/usePoolDetailsData';
import { useExplorePoolActions } from './hooks/useExplorePoolActions';

type Props = {
  pool: PoolDetailsData;
  onBack?: () => void;
};

export const PoolDetailsHeader = memo(function PoolDetailsHeader({ pool, onBack }: Props) {
  const router = useRouter();
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;
  const { handleSwapClick, handleCreatePositionClick } = useExplorePoolActions(pool);

  const handleBackClick = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      router.push('/');
    }
  }, [router, onBack]);

  return (
    <VStack alignItems="flex-start" gap={2} paddingBottom={isMobile ? 1 : 3}>
      {/* Breadcrumb */}
      <HStack alignItems="center" gap={0.75}>
        <TextHeadline
          as="button"
          color="fgMuted"
          onClick={handleBackClick}
          style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
        >
          Pools
        </TextHeadline>
        <Icon name="caretRight" size="s" color="fgMuted" />
        <TextHeadline as="span">{pool.name}</TextHeadline>
      </HStack>

      {/* Main header row */}
      <HStack width="100%" alignItems="center" justifyContent="space-between">
        {/* Left side: tokens and info */}
        <HStack alignItems="center" gap={1.5}>
          <ExploreListTokensMedia
            baseSymbol={pool.baseSymbol}
            quoteSymbol={pool.quoteSymbol}
            size={isMobile ? 'xl' : 'xxxl'}
          />
          <VStack gap={0.5} alignItems="flex-start">
            <TextTitle1 as="h1">{pool.name}</TextTitle1>
            <HStack gap={0.5}>
              {/* Fee tag */}
              <Tag intent="informational" colorScheme="gray">
                {format({ value: pool.feePercent, type: DisplayType.Percentage })}
              </Tag>

              {/* USDC Rewards tag */}
              {pool.hasUsdcRewards && (
                <HStack
                  background="bgPrimaryWash"
                  paddingX={0.5}
                  paddingY={0.25}
                  borderRadius={100}
                  gap={0.5}
                  alignItems="center"
                >
                  <Icon name="earn" size="s" color="fgPrimary" />
                  <TextLabel1 as="span" color="fgPrimary">
                    {pool.rewardRate}% USDC
                  </TextLabel1>
                </HStack>
              )}
            </HStack>
          </VStack>
        </HStack>

        {/* Right side: action buttons (desktop only) */}
        {!isMobile && (
          <ButtonGroup>
            <Button variant="secondary" compact onClick={handleSwapClick}>
              Swap
            </Button>
            <Button
              variant="primary"
              compact
              onClick={handleCreatePositionClick}
              testID="create-position-button"
            >
              Create position
            </Button>
          </ButtonGroup>
        )}
      </HStack>
    </VStack>
  );
});
